import React, { useEffect, useRef } from 'react';
import cx from 'classnames';
import styles from './AnimatedBorderBox.module.scss';

export interface AnimatedBorderBoxProps {
  children: React.ReactNode;
  className?: string;
  borderRadius?: number;
  strokeWidth?: number;
  animationDuration?: number;
  gradientStartColor?: string;
  gradientEndColor?: string;
  gradientId?: string;
  disabled?: boolean;
  iterations?: number;
  pathClassName?: string;
}

const AnimatedBorderBox: React.FC<AnimatedBorderBoxProps> = ({
  children,
  className,
  borderRadius = 12,
  strokeWidth = 1,
  animationDuration = 100,
  gradientStartColor = 'var(--blue-base)',
  gradientEndColor = 'var(--blue-border)',
  gradientId = 'borderGradient',
  disabled = false,
  iterations = Infinity,
  pathClassName
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement>(null);

  const updatePath = () => {
    if (!containerRef.current || !svgRef.current || !pathRef.current || disabled) return;

    const width = containerRef.current.offsetWidth;
    const height = containerRef.current.offsetHeight;
    const parentWidth = width + strokeWidth * 2;
    const parentHeight = height + strokeWidth * 2;

    // Position SVG to match the padded container
    svgRef.current.setAttribute('width', parentWidth.toString());
    svgRef.current.setAttribute('height', parentHeight.toString());

    // Create rounded rect path manually (clockwise) - offset by strokeWidth for padding
    const pathX = strokeWidth;
    const pathY = strokeWidth;
    const pathWidth = width;
    const pathHeight = height;

    const d = `
      M${pathX + borderRadius},${pathY}
      H${pathX + pathWidth - borderRadius}
      Q${pathX + pathWidth},${pathY} ${pathX + pathWidth},${pathY + borderRadius}
      V${pathY + pathHeight - borderRadius}
      Q${pathX + pathWidth},${pathY + pathHeight} ${pathX + pathWidth - borderRadius},${
      pathY + pathHeight
    }
      H${pathX + borderRadius}
      Q${pathX},${pathY + pathHeight} ${pathX},${pathY + pathHeight - borderRadius}
      V${pathY + borderRadius}
      Q${pathX},${pathY} ${pathX + borderRadius},${pathY}
    `;
    pathRef.current.setAttribute('d', d.trim());

    // Animate
    const length = pathRef.current.getTotalLength();
    pathRef.current.style.strokeDasharray = length.toString();
    pathRef.current.style.strokeDashoffset = length.toString();

    // Stop any existing animation
    pathRef.current.getAnimations().forEach(animation => animation.cancel());

    // Start new animation
    pathRef.current.animate([{ strokeDashoffset: length.toString() }, { strokeDashoffset: '0' }], {
      duration: animationDuration,
      iterations,
      easing: 'linear'
    });
  };

  useEffect(() => {
    if (disabled) return;

    // Initialize animation with a slight delay to ensure DOM is ready
    const timer = setTimeout(updatePath, 100);

    // Handle resize using ResizeObserver for more precise detection
    let resizeObserver: ResizeObserver | null = null;

    if (containerRef.current) {
      resizeObserver = new ResizeObserver(() => updatePath());
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timer);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [borderRadius, strokeWidth, animationDuration, disabled]);

  return (
    <div className={cx(styles.animatedBorderBox, className, `p-[${strokeWidth}px]`)}>
      <div ref={containerRef} className={cx(styles.contentWrapper, `${borderRadius}px`)}>
        {children}
      </div>
      {!disabled && (
        <svg ref={svgRef} className={styles.borderSvg}>
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor={gradientStartColor} />
              <stop offset="100%" stopColor={gradientEndColor} />
            </linearGradient>
          </defs>
          <path
            ref={pathRef}
            className={cx(styles.tracePath, pathClassName)}
            style={{
              stroke: `url(#${gradientId})`,
              strokeWidth: strokeWidth
            }}
          />
        </svg>
      )}
    </div>
  );
};

export default AnimatedBorderBox;
